import { Node } from "@xyflow/react";
import { Search, Target } from "lucide-react";
import { useState, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export interface MultiTargetSelectorProps {
  open: boolean;
  onClose: () => void;
  nodes: Node[];
  sourceNodeId: string;
  selectedTargets: string[];
  onTargetsChange: (targets: string[]) => void;
  onConfirm: () => void;
}

export const MultiTargetSelector = ({
  open,
  onClose,
  nodes,
  sourceNodeId,
  selectedTargets,
  onTargetsChange,
  onConfirm,
}: MultiTargetSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const availableNodes = nodes.filter(node => node.id !== sourceNodeId);
  const filteredNodes = availableNodes.filter(node => {
    const label = typeof node.data?.label === 'string' ? node.data.label : '';
    const nodeType = node.type || '';
    const nodeId = node.id;
    
    return label.toLowerCase().includes(searchTerm.toLowerCase()) ||
           nodeType.toLowerCase().includes(searchTerm.toLowerCase()) ||
           nodeId.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleTargetToggle = useCallback((nodeId: string) => {
    const newTargets = selectedTargets.includes(nodeId)
      ? selectedTargets.filter(id => id !== nodeId)
      : [...selectedTargets, nodeId];
    onTargetsChange(newTargets);
  }, [selectedTargets, onTargetsChange]);

  const handleSelectAll = useCallback(() => {
    const allNodeIds = filteredNodes.map(node => node.id);
    onTargetsChange(allNodeIds);
  }, [filteredNodes, onTargetsChange]);

  const handleClearAll = useCallback(() => {
    onTargetsChange([]);
  }, [onTargetsChange]);

  const getNodeTypeColor = (nodeType?: string) => {
    const colors: Record<string, string> = {
      'input': '#10b981',
      'output': '#ef4444',
      'group': '#8b5cf6',
      'process': '#3b82f6',
      'decision': '#f59e0b',
      'default': '#64748b',
    };
    return colors[nodeType || 'default'] || colors.default;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Select Multiple Targets
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 flex-1 min-h-0">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search nodes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                disabled={filteredNodes.length === 0}
              >
                Select All ({filteredNodes.length})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearAll}
                disabled={selectedTargets.length === 0}
              >
                Clear All
              </Button>
            </div>
            <Badge variant="secondary">
              {selectedTargets.length} selected
            </Badge>
          </div>

          {/* Node List */}
          <div className="flex-1 overflow-auto border rounded-md">
            {filteredNodes.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No nodes found
              </div>
            ) : (
              <div className="p-2 space-y-2">
                {filteredNodes.map((node) => (
                  <div
                    key={node.id}
                    className="flex items-center gap-3 p-3 rounded-md hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleTargetToggle(node.id)}
                  >
                    <Checkbox
                      checked={selectedTargets.includes(node.id)}
                      onChange={() => handleTargetToggle(node.id)}
                    />
                    
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: getNodeTypeColor(node.type) }}
                    />
                      <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">
                        {typeof node.data?.label === 'string' ? node.data.label : node.id}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Type: {node.type || 'default'} • ID: {node.id}
                      </div>
                    </div>

                    {node.type && (
                      <Badge variant="outline" className="text-xs">
                        {node.type}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={onConfirm}
              disabled={selectedTargets.length === 0}
            >
              Connect to {selectedTargets.length} Target{selectedTargets.length !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
